import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import SEO from "@/components/SEO";
import FAQSchema from "@/components/FAQSchema";
import ContactModal from "@/components/new-landing/ContactModal";
import { trackEvent } from "@/utils/analytics";

const NAVY = "#002178";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const whatAiNativeMeans = [
  {
    number: "01",
    title: "Data Analysis, Instantly",
    description:
      "No waiting for another team. We analyze data ourselves, in real-time, and turn it into decisions the same day. Not next sprint. Today.",
  },
  {
    number: "02",
    title: "Instant Discovery Synthesis",
    description:
      "Customer signals, competitive intelligence, market analysis. What used to take 2-week research sprints now takes hours. Insights are fresh, not stale.",
  },
  {
    number: "03",
    title: "Rapid Prototyping",
    description:
      "We validate ideas in days. Working prototypes, not slide decks. Kill bad ideas fast, scale winners faster. A functional prototype in minutes, not months.",
  },
  {
    number: "04",
    title: "Real-time Feedback Loops",
    description:
      "Measurement isn't a monthly dashboard review. It's continuous. The gap between action and outcome shrinks to near-zero. We know what's working before the quarterly review.",
  },
  {
    number: "05",
    title: "Decisions at Scale",
    description:
      "We share knowledge systematically across our team through shared frameworks and tools. Every engagement benefits from the collective intelligence of every engagement before it. We bring this consistency to your team too.",
  },
  {
    number: "06",
    title: "Effortless ELT Reporting",
    description:
      "Keeping the board and executive team informed doesn't take weeks of report writing. Consistent, data-backed updates. Fast feedback implementation. Your ELT always knows where product stands, without the overhead.",
  },
];

const whyLegacyFails = [
  {
    number: "01",
    title: "The PM bottleneck",
    description:
      "Engineers using AI write code faster than ever. But product managers can't define problems, write specs, or prioritize fast enough to keep up. The constraint has flipped. Your product org is now the slowest part of your company.",
  },
  {
    number: "02",
    title: "Everything runs on consensus",
    description:
      "Decisions that should take hours take weeks of alignment meetings. By the time everyone agrees, the market has moved.",
  },
  {
    number: "03",
    title: "Data on Someone Else's Backlog",
    description:
      "Product teams wait for data teams, analytics teams, or engineering to pull numbers. Insights arrive too late to matter. You can't be data-driven when data doesn't flow.",
  },
  {
    number: "04",
    title: "Knowledge Trapped in Heads",
    description:
      "Person-to-person storytelling creates broken telephone. Context is lost. Decisions lack consistency. New team members start from zero.",
  },
  {
    number: "05",
    title: "Teams Won't Upskill Alone",
    description:
      "Without a growth mindset and AI-native tools, your team falls further behind every quarter. The gap between AI-native teams and legacy teams is widening, not closing.",
  },
];

const shiftRows = [
  {
    dimension: "Discovery",
    legacy: "Quarterly user research, 2-week sprints",
    aiNative: "Continuous signal detection, synthesis in hours",
  },
  {
    dimension: "Decision speed",
    legacy: "Weeks of consensus building",
    aiNative: "Data-backed decisions in days",
  },
  {
    dimension: "Prototyping",
    legacy: "3-6 month roadmap to first version",
    aiNative: "Working prototypes in days",
  },
  {
    dimension: "Measurement",
    legacy: "Monthly dashboards, lagging indicators",
    aiNative: "Real-time behavioral analytics",
  },
  {
    dimension: "Knowledge",
    legacy: "Tribal, person-to-person",
    aiNative: "Systematic, shared across every engagement",
  },
  {
    dimension: "ELT visibility",
    legacy: "Quarterly updates, report writing overhead",
    aiNative: "Continuous, effortless, data-backed",
  },
  {
    dimension: "The outcome",
    legacy: "Busy work, activity metrics",
    aiNative: "Financial results: speed, quality, efficiency, thoroughness",
  },
];

const aiTheater = [
  '"We use AI tools" on the careers page',
  "Sporadic ChatGPT usage by individuals",
  "AI as a feature bolt-on to existing products",
  "PMs still request engineering help for prototypes",
  'Measuring "AI features shipped"',
];

const aiNativeReal = [
  "Every workflow systematically redesigned around AI",
  "PMs prototype and validate independently",
  "Continuous measurement, real-time signal detection",
  "Shared decision frameworks that scale across engagements",
  "Measuring what matters: speed, quality, efficiency, and financial outcomes",
];

const faqs = [
  {
    question: "What is AI-native product management?",
    answer:
      "AI-native product management means every product workflow, from discovery to measurement, is redesigned around AI. It's not about using ChatGPT occasionally. It's about fundamentally changing how product teams discover opportunities, make decisions, build, and measure results. At FractionalCPO, we operate this way and bring it to every engagement.",
  },
  {
    question: "How does AI-native leadership deliver better results?",
    answer:
      "Four measurable outcomes: speed (decisions in days not weeks), quality (data-backed, not gut-driven), efficiency (more ground covered with the same team), and thoroughness (nothing falls through the cracks). These compound into financial results that show up in board reports.",
  },
  {
    question: "Do we need to be a technology company to benefit?",
    answer:
      "No. Any company with a product team benefits from AI-native practices. Technology companies across SaaS, platforms, marketplaces, and fintech see the fastest impact, but the principles apply wherever product drives revenue.",
  },
  {
    question: "What if our team has no AI experience?",
    answer:
      "That's normal. Most product teams are still running pre-AI playbooks. We bring the expertise and transfer it through hands-on embedded leadership. We sometimes leave teams with tools and frameworks that continue working after our engagement ends.",
  },
  {
    question: "Is this just about implementing AI features?",
    answer:
      "No. AI-native is about how your team works, not what your product does. You can build a non-AI product with an AI-native team and still move 10x faster than competitors. The competitive advantage is in execution, not features.",
  },
];

const AI = () => {
  const [isContactOpen, setIsContactOpen] = useState(false);

  return (
    <PageLayout>
      <SEO
        title="AI-Native Product Leadership"
        description="Engineering moves at AI speed. Product management is the bottleneck. FractionalCPO embeds AI-native product leaders who deliver financial results faster."
        path="/ai"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          dateModified: "2026-03-25",
        }}
      />

      {/* Section 1: Hero */}
      <section className="py-24 md:py-40 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-8 font-medium"
          >
            AI Leadership
          </motion.p>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[clamp(2.5rem,8vw,6rem)] font-light leading-[0.95] tracking-[-0.03em] mb-8"
          >
            AI-Native
            <br />
            <span className="text-[#002178] font-medium">
              Product Leadership
            </span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mb-12"
          >
            <p className="text-lg text-black/60 leading-relaxed">
              Engineering already moves at AI speed. Product management is the
              bottleneck. We fix that.
            </p>
          </motion.div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <button
              onClick={() => { trackEvent('contact_click', { source: 'hero' }); setIsContactOpen(true); }}
              className="inline-block px-8 py-4 bg-[#002178] text-white font-medium hover:scale-105 transition-transform"
            >
              Let's chat
            </button>
          </motion.div>
        </div>
      </section>

      {/* Section 2: What AI-Native Means */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
              >
                The Definition
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-8"
              >
                What AI-native
                <br />
                actually means
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-black/60 leading-relaxed"
              >
                This isn't about using ChatGPT occasionally. AI-native means
                every workflow is redesigned around AI. The result: more ground
                covered, faster decisions, and financial outcomes that show up in
                board reports.
              </motion.p>
            </div>

            <div className="space-y-0">
              {whatAiNativeMeans.map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-black/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-[#002178]/30 font-mono text-sm">
                      {item.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 group-hover:text-[#002178] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-black/50 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Why Legacy Leadership Fails */}
      <section
        className="py-24 md:py-32 px-6 md:px-12 lg:px-24"
        style={{ backgroundColor: NAVY }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="md:sticky md:top-32">
              <motion.p
                {...fadeUp}
                className="text-sm uppercase tracking-[0.3em] text-white/50 mb-4 font-medium"
              >
                The Problem
              </motion.p>
              <motion.h2
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] text-white mb-8"
              >
                Why legacy product
                <br />
                leadership fails in
                <br />
                the AI era
              </motion.h2>
              <motion.p
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white/60 leading-relaxed"
              >
                Engineering teams adopted AI and got faster overnight. Product
                teams didn't. The playbook that worked for the last decade is now
                the thing holding your company back.
              </motion.p>
            </div>

            <div className="space-y-0">
              {whyLegacyFails.map((item, i) => (
                <motion.div
                  key={i}
                  {...fadeUp}
                  transition={{ delay: i * 0.1 }}
                  className="group py-8 border-b border-white/10"
                >
                  <div className="flex gap-6 items-baseline">
                    <span className="text-white/20 font-mono text-sm">
                      {item.number}
                    </span>
                    <div>
                      <h3 className="text-xl md:text-2xl font-medium mb-2 text-white group-hover:text-white/80 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-white/50 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: The Shift */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
          >
            The Transformation
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-16"
          >
            The shift
          </motion.h2>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="overflow-x-auto"
          >
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b-2 border-black/10">
                  <th className="text-left py-4 pr-8 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[20%]"></th>
                  <th className="text-left py-4 pr-8 text-sm uppercase tracking-[0.2em] text-black/40 font-medium w-[40%]">
                    Legacy Product Leadership
                  </th>
                  <th className="text-left py-4 text-sm uppercase tracking-[0.2em] text-[#002178] font-medium w-[40%]">
                    AI-Native (FractionalCPO)
                  </th>
                </tr>
              </thead>
              <tbody>
                {shiftRows.map((row, i) => (
                  <motion.tr
                    key={i}
                    {...fadeUp}
                    transition={{ delay: 0.3 + i * 0.05 }}
                    className="border-b border-black/10"
                  >
                    <td className="py-5 pr-8 font-medium text-sm">
                      {row.dimension}
                    </td>
                    <td className="py-5 pr-8 text-black/50">{row.legacy}</td>
                    <td className="py-5 text-black/80 font-medium">
                      {row.aiNative}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* Section 5: AI Theater vs Real Results */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F5F5F0]">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            {...fadeUp}
            className="text-sm uppercase tracking-[0.3em] text-[#002178] mb-4 font-medium"
          >
            The Difference
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[clamp(2rem,5vw,3.5rem)] font-light leading-[1.1] tracking-[-0.02em] mb-6"
          >
            AI theater vs real results
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-black/60 leading-relaxed max-w-2xl mb-16"
          >
            Most companies claim to be AI-native. Few actually are. Here's how
            to tell the difference.
          </motion.p>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* AI Theater column */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-8 md:p-10"
            >
              <h3 className="text-sm uppercase tracking-[0.2em] text-black/40 font-medium mb-8">
                AI Theater
              </h3>
              <ul className="space-y-5">
                {aiTheater.map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-4 items-start text-black/50"
                  >
                    <span className="text-black/20 mt-0.5">&#x2715;</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* AI-Native column */}
            <motion.div
              {...fadeUp}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-[#002178] p-8 md:p-10"
            >
              <h3 className="text-sm uppercase tracking-[0.2em] text-white/50 font-medium mb-8">
                FractionalCPO (AI-Native)
              </h3>
              <ul className="space-y-5">
                {aiNativeReal.map((item, i) => (
                  <li
                    key={i}
                    className="flex gap-4 items-start text-white/80"
                  >
                    <span className="text-white/40 mt-0.5">&#x2713;</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 max-w-3xl"
          >
            <p className="text-black/60 leading-relaxed">
              We're not about AI theater. We're about the tangible results an
              AI-native team delivers. More ground covered. Better decisions,
              faster. Financial outcomes that show up in board reports.{" "}
              <Link
                to="/case-study"
                className="text-[#002178] font-medium underline underline-offset-4 hover:text-[#002178]/70 transition-colors"
              >
                See what AI-native leadership delivers &rarr;
              </Link>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 6: FAQ */}
      <div className="bg-[#F5F5F0]">
        <FAQSchema faqs={faqs} heading="Frequently Asked Questions" />
      </div>

      <ContactModal open={isContactOpen} onOpenChange={setIsContactOpen} />
    </PageLayout>
  );
};

export default AI;
